document.addEventListener('DOMContentLoaded', function() {
  
  /**
   * -------------------------------------------------------------------------
   * Cart API Service
   * -------------------------------------------------------------------------
   */
  const CartAPI = {
    addItem(id, quantity = 1) {
      return fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id, quantity }),
      }).then(res => {
        if (!res.ok) throw new Error('Error adding to cart');
        return res.json();
      });
    },
    getCart() {
      return fetch('/cart.js').then(res => res.json());
    },
    getDrawerHtml() {
      return fetch('/cart?view=cart-drawer').then(res => res.text());
    }
  };

  /**
   * -------------------------------------------------------------------------
   * Cart Drawer UI Manager
   * -------------------------------------------------------------------------
   */
  const CartDrawer = {
    init() {
      // Delegate global clicks (one listener for all inputs)
      document.addEventListener('click', (e) => {
        // Quick Add Button
        const quickAddBtn = e.target.closest('.quick-add-btn:not(.quick-add-btn--sold-out)');
        if (quickAddBtn) {
          ProductCard.handleQuickAdd(e, quickAddBtn);
        }

        // Option Buttons (Main Product & Related Product)
        const optionBtn = e.target.closest('.option-value, .related-option-value');
        if (optionBtn) {
           ProductCard.handleOptionClick(e, optionBtn);
        }
      });
    },

    updateCartUI(cart) {
      // 1. Update Badge
      const countEl = document.getElementById('CartCount');
      if (countEl) countEl.textContent = cart.item_count;
      
      // 2. Determine where to send user
      const cartType = document.body.dataset.cartType || (document.getElementById('CartDrawer') ? 'drawer' : 'page');
      
      if (cartType === 'drawer') {
        this.openDrawer();
        this.refreshDrawerContent();
      } else {
        window.location.href = '/cart';
      }
    },

    openDrawer() {
      const drawer = document.getElementById('CartDrawer');
      const overlay = document.getElementById('CartDrawerOverlay');
      if (drawer && overlay) {
        drawer.classList.add('open');
        overlay.classList.add('fade-in-drawer');
        setTimeout(() => overlay.classList.add('active'), 200);
        document.body.style.overflow = 'hidden';
      }
    },

    refreshDrawerContent() {
      const content = document.getElementById('CartDrawerContent');
      if (!content) return;
      
      CartAPI.getDrawerHtml().then(html => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const newContent = doc.getElementById('CartDrawerContent');
        const newTotal = doc.getElementById('CartDrawerTotal');
        const totalEl = document.getElementById('CartDrawerTotal');
        
        if (newContent) content.innerHTML = newContent.innerHTML;
        if (newTotal && totalEl) totalEl.innerHTML = newTotal.innerHTML;
      });
    }
  };

  /**
   * -------------------------------------------------------------------------
   * Product Card Logic
   * -------------------------------------------------------------------------
   */
  const ProductCard = {
    init() {
      // 1. Initialize existing products on load
      this.initAllVariants();

      // 2. Watch for Infinite Scroll (new products added to DOM)
      const productGrid = document.querySelector('[class*="product-list"], [class*="collection-grid"], #product-list'); 
      if (productGrid) {
        const observer = new MutationObserver((mutations) => {
          const hasAddedNodes = mutations.some(m => m.addedNodes.length > 0);
          if (hasAddedNodes) this.initAllVariants();
        });
        observer.observe(productGrid, { childList: true, subtree: true });
      }
    },

    initAllVariants() {
      // Find all product blocks that haven't been initialized yet
      const blocks = document.querySelectorAll('.product-list__product-block[data-product-variants]:not([data-variants-initialized])');
      
      blocks.forEach(block => {
        block.dataset.variantsInitialized = 'true';
        
        try {
          const variants = JSON.parse(block.dataset.productVariants);
          const quickAddBtn = block.querySelector('.quick-add-btn');
          
          if (!quickAddBtn) return;

          // STEP 1: Determine Initial State based on Liquid
          // Liquid already decided which variant ID is on the button.
          // We use that ID to reverse-engineer which options are selected.
          const initialVariantId = parseInt(quickAddBtn.dataset.variantId);
          
          let currentVariant;
          
          if (initialVariantId) {
             currentVariant = variants.find(v => v.id === initialVariantId);
          } 
          
          // Fallback: If Liquid said "Sold Out" (no ID), use the first variant in JSON just to get option values
          if (!currentVariant) {
            currentVariant = variants[0];
          }

          if (currentVariant) {
            // Create a state object: { option1: "Blue", option2: "Large" }
            const selectedOptions = {};
            currentVariant.options.forEach((val, idx) => {
              selectedOptions[`option${idx + 1}`] = val;
              
              // Visually activate the buttons matching this variant
              // (Handles both main options and related-options)
              const btn = block.querySelector(`
                .option-value[data-option="${idx+1}"][data-value="${CSS.escape(val)}"],
                .related-option-value[data-option="${idx+1}"][data-value="${CSS.escape(val)}"]
              `);
              
              // Only add 'active' if the button exists and the Liquid button wasn't disabled (sold out)
              if (btn && !quickAddBtn.classList.contains('quick-add-btn--sold-out')) {
                btn.classList.add('active');
              }
            });

            // Store state in DOM
            block.dataset.currentSelection = JSON.stringify(selectedOptions);

            // STEP 2: IMMEDIATE AVAILABILITY CHECK (The Fix)
            // This runs on page load to disable invalid sizes immediately
            this.checkOptionAvailability(block, variants, selectedOptions);
          }

        } catch (e) {
          console.error('Error parsing variants:', e);
        }
      });
    },

    handleOptionClick(e, btn) {
      if (btn.classList.contains('disabled') || btn.disabled) return;
      e.preventDefault();
      e.stopPropagation();

      const block = btn.closest('.product-list__product-block');
      if (!block) return;

      const variants = JSON.parse(block.dataset.productVariants);
      const clickedOptionIndex = btn.dataset.option; // e.g., "1" or "2"
      const clickedValue = btn.dataset.value;

      // 1. Update Visuals: Toggle active class
      const siblings = block.querySelectorAll(`
        .option-value[data-option="${clickedOptionIndex}"], 
        .related-option-value[data-option="${clickedOptionIndex}"]
      `);
      siblings.forEach(b => b.classList.remove('active'));
      btn.classList.add('active');

      // 2. Update State
      let selectedOptions = JSON.parse(block.dataset.currentSelection || '{}');
      selectedOptions[`option${clickedOptionIndex}`] = clickedValue;
      block.dataset.currentSelection = JSON.stringify(selectedOptions);

      // 3. Find Matching Variant
      const matchingVariant = variants.find(v => {
        return v.options.every((optVal, idx) => optVal === selectedOptions[`option${idx + 1}`]);
      });

      // 4. Update "Add to Bag" Button
      const quickAddBtn = block.querySelector('.quick-add-btn');
      this.updateAddButton(quickAddBtn, matchingVariant);
      
      // 5. Check Availability for ALL other buttons based on this new selection
      this.checkOptionAvailability(block, variants, selectedOptions);
    },

    /**
     * Loops through every option button in the card.
     * Temporarily combines that button's value with the *currently selected* 
     * values of the other options. If that combination doesn't exist 
     * or is unavailable in the JSON, it disables the button.
     */
    checkOptionAvailability(block, variants, currentSelection) {
      const allButtons = block.querySelectorAll('.option-value, .related-option-value');

      allButtons.forEach(btn => {
        const optionIndex = btn.dataset.option;
        const optionValue = btn.dataset.value;
        
        // If we clicked this button, what would the variant look like?
        // We keep all other current selections, but swap in this button's value.
        const hypotheticalSelection = { 
          ...currentSelection, 
          [`option${optionIndex}`]: optionValue 
        };

        // Find if this hypothetical variant exists and is available
        const match = variants.find(v => {
          return v.options.every((optVal, idx) => optVal === hypotheticalSelection[`option${idx + 1}`]);
        });

        if (match && match.available) {
          btn.classList.remove('disabled');
          btn.disabled = false;
        } else {
          btn.classList.add('disabled');
          // We avoid setting btn.disabled = true to allow clicks if you want "Email when available" logic later, 
          // otherwise uncomment the next line:
          // btn.disabled = true; 
        }
      });
    },

    updateAddButton(btn, variant) {
      if (!btn) return;
      const btnText = btn.querySelector('.quick-add-btn__text');
      
      if (variant && variant.available) {
        btn.dataset.variantId = variant.id;
        btn.disabled = false;
        btn.classList.remove('quick-add-btn--sold-out');
        btn.setAttribute('aria-label', `Quick add to cart`);
        if (btnText) btnText.textContent = 'ADD TO BAG';
      } else {
        btn.dataset.variantId = '';
        btn.disabled = true;
        btn.classList.add('quick-add-btn--sold-out');
        btn.setAttribute('aria-label', `Sold out`);
        if (btnText) btnText.textContent = 'SOLD OUT';
      }
    },

    handleQuickAdd(e, btn) {
      e.preventDefault();
      e.stopPropagation();

      const variantId = btn.dataset.variantId;
      if (!variantId) return;

      const btnText = btn.querySelector('.quick-add-btn__text');
      const loadingText = btn.querySelector('.quick-add-btn__loading');

      // Loading UI
      btn.disabled = true;
      if (btnText) btnText.style.display = 'none';
      if (loadingText) loadingText.style.display = 'inline-block';

      CartAPI.addItem(variantId, 1)
        .then(() => CartAPI.getCart())
        .then(cart => {
          CartDrawer.updateCartUI(cart);
        })
        .catch(error => {
          console.error('Add to cart error:', error);
          alert('Unable to add item to cart. Please try again.');
        })
        .finally(() => {
          // Reset UI
          btn.disabled = false;
          if (btnText) btnText.style.display = 'inline';
          if (loadingText) loadingText.style.display = 'none';
        });
    }
  };

  // Start logic
  CartDrawer.init();
  ProductCard.init();
});